// src/cmps/comment/CommentPreview.jsx
import { useState } from 'react'
import { CommentEdit } from './CommentEdit'
import { utilService } from '../../services/util.service'
import {
  commentImages,
} from '../../services/images.service'
import { isCommentOwnerOrPrivileged } from '../../services/permission.service'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { openNotRegisteredPopup } from '../../store/slices/ui.slice'
import { useDispatch } from 'react-redux'
import { useAuthorMap } from '../../hooks/useAuthorMap'
import { HeartIcon } from '../ui/icons/shared'
import { Button } from '../ui/elements'

/**
 * קובעת מחלקות Tailwind לעומק התגובה (depth).
 * אם depth = 0 => ללא הזחה, אחרת => border מימין + padding.
 */
function getDepthClasses(depth) {
  return depth > 0 ? 'border-r-2 border-[#353535] p-4' : ''
}

export function CommentPreview({
  comment,
  users,
  loggedInUser,
  onAddComment,
  onEditComment,
  onRemoveComment,
  onToggleLike,
  depth = 0,
  maxDepth = 3,
  isTopComment = false, // prop חדש שמסמן אם זו התגובה המובילה
}) {
  const [replyContent, setReplyContent] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [showAllReplies, setShowAllReplies] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false) // תפריט 3 נקודות (עריכה/מחיקה)
  const dispatch = useDispatch()
  if (!comment || !comment.content) return null

  // מידע על המשתמש
  const authorMap = useAuthorMap()
  const user = authorMap[comment.userId] || {}

  const isLikedByUser =
    Array.isArray(comment.likes) && loggedInUser
      ? comment.likes.includes(loggedInUser._id)
      : false

  // חישוב "לפני X זמן"
  const timeAgoStr = utilService.timeAgo(comment.createdAt)

  // תתי תגובות (replies)
  const replies = comment.replies || []
  const displayedReplies = showAllReplies ? replies : replies.slice(0, 3)

  // פונקציות עזר
  function handleSaveEdit(updatedContent) {
    if (!updatedContent.trim()) return
    onEditComment(comment._id, updatedContent)
    setIsEditing(false)
  }

  function handleRemove() {
    onRemoveComment(comment._id)
  }

  function handleLike() {
    onToggleLike(comment._id)
  }

  function handleReplyToggle() {
    if (!loggedInUser) {
      toast.error('עליך להתחבר כדי להוסיף תת תגובה.')
      dispatch(openNotRegisteredPopup())
      return
    }
    setReplyContent((prev) => (prev == null ? '' : null))
  }

  function handleReplySubmit() {
    if (!replyContent || !replyContent.trim()) return
    onAddComment(replyContent, comment._id)
    setReplyContent(null)
  }

  function toggleMenu() {
    setIsMenuOpen((prev) => !prev)
  }

  function closeMenu() {
    setIsMenuOpen(false)
  }

  // במצב עריכה
  if (isEditing) {
    return (
      <div
        className={`p-4 bg-gray-700 text-white rounded-md mb-6 ${getDepthClasses(
          depth
        )}`}
      >
        <CommentEdit
          initialContent={comment.content}
          onSave={handleSaveEdit}
          onCancel={() => setIsEditing(false)}
        />
      </div>
    )
  }

  // מחלקת container – בהתאם אם זו תגובה מובילה או לא
  const containerClasses = `
    w-full
    ${
      isTopComment
        ? 'bg-gradient-to-b from-[#252525] to-[#1b1b1b]'
        : 'bg-[#1b1b1b]'
    }
    relative
    ${getDepthClasses(depth)}
  `

  return (
    <section className={containerClasses}>
      {/* סימון "תגובה מובילה" רק אם הפרופ isTopComment אמתי */}
      {isTopComment && (
        <div
          className="mb-2 inline-block bg-no-repeat bg-center bg-cover text-white text-xs px-2 py-1 rounded"
          style={{ backgroundImage: `url(${commentImages.pouplarComment})` }}
        >
          התגובה המובילה
        </div>
      )}

      <div>
        <div className="flex flex-col gap-4 px-4">
          <div className="flex items-center justify-between gap-4">
            <Link
              to={`/user/${user.username}`}
              className="flex items-center gap-4"
            >
              <img
                src={user.image}
                alt={user.nickname}
                className="w-[74px] h-[74px] rounded-full object-cover"
              />
              <div className="flex flex-col">
                <h4 className="text-xl text-white">{user.nickname}</h4>
                <span className="text-xs text-muted">{timeAgoStr}</span>
              </div>
            </Link>
            <p className="text-xl md:w-[70%] hidden lg:block">
              {comment.content}
            </p>

            {loggedInUser &&
              isCommentOwnerOrPrivileged(loggedInUser, comment) && (
                <div className="relative">
                  <img
                    src={commentImages.ellipsisBtn}
                    alt="More Options"
                    className="w-5 h-5 cursor-pointer"
                    onClick={toggleMenu}
                  />
                  {isMenuOpen && (
                    <div className="absolute top-6 left-0 bg-[#2b2b2b] rounded shadow-lg flex flex-col items-end z-10 min-w-[80px]">
                      <button
                        onClick={() => {
                          setIsEditing(true)
                          closeMenu()
                        }}
                        className="w-full text-right px-3 py-2 hover:bg-[#3a3a3a]"
                      >
                        ערוך
                      </button>
                      <button
                        onClick={() => {
                          handleRemove()
                          closeMenu()
                        }}
                        className="w-full text-right px-3 py-2 hover:bg-[#3a3a3a] text-red-400"
                      >
                        מחק
                      </button>
                    </div>
                  )}
                </div>
              )}
          </div>
          <p className="text-xl md:w-[70%] lg:hidden">{comment.content}</p>
        </div>

        {/* אזור תחתון (הגב + לייק) */}
        <div className="flex items-center justify-end gap-6 p-2">
          {depth < maxDepth && (
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={handleReplyToggle}
            >
              <span className="text-white text-base">הגב</span>
              <img
                src={commentImages.replyBtn}
                alt="Reply"
                className="w-[19px] h-[19px] rotate-180"
              />
            </div>
          )}
          {/* לייקים */}
          <div className="flex items-center gap-2">
            <span className="text-white text-base">
              {comment.likes?.length || 0}
            </span>

            <HeartIcon
              onClick={handleLike}
              className={`w-5 h-5 transition-colors duration-200 ${
                isLikedByUser ? 'text-primary' : 'hover:text-primary'
              }`}
            />
          </div>
        </div>

        {/* אזור תשובה (reply) */}
{replyContent != null && (
  <div className="mt-4 flex items-center gap-3">
    <input
      type="text"
      value={replyContent}
      onChange={(e) => setReplyContent(e.target.value)}
      className="flex-1 rounded-md bg-zinc-800 text-white placeholder:text-zinc-400 border border-zinc-700 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary"
      placeholder="הקלד תשובה..."
    />
    <Button
      text="הגב חזרה"
      width={100}
      height={50}
      fontSize="text-base"
      textColor="text-white"
      bgColor="fill-primary"
      onClick={handleReplySubmit}
      disabled={!replyContent.trim()}
      title="שלח תגובה"
      ariaLabel="שלח תגובה חדשה לכתבה"
    />
  </div>
)}

      </div>

      {/* תתי תגובות */}
      {displayedReplies.length > 0 && (
        <div className="space-y-3 mt-3">
          {displayedReplies.map((reply) => (
            <CommentPreview
              key={reply._id}
              comment={reply}
              users={users}
              loggedInUser={loggedInUser}
              onAddComment={onAddComment}
              onEditComment={onEditComment}
              onRemoveComment={onRemoveComment}
              onToggleLike={onToggleLike}
              depth={depth + 1}
              maxDepth={maxDepth}
            />
          ))}
        </div>
      )}

      {/* כפתור "הצג עוד" */}
      {replies.length > 3 && !showAllReplies && (
        <button
          onClick={() => setShowAllReplies(true)}
          className="text-primary hover:underline mt-2 text-sm"
        >
          הצג עוד תגובות ({replies.length - 3} נוספות)
        </button>
      )}
    </section>
  )
}
